export const CONTROL_SLIDE_IMAGE_COUNT = 10;

export function normalizeControlIdentifier(value) {
  const text = String(value ?? '').trim();
  if (!/^\d+$/.test(text)) {
    return null;
  }
  const identifier = Number(text);
  return Number.isSafeInteger(identifier) && identifier > 0 ? identifier : null;
}

export function controlIdentifierForImage(image) {
  return normalizeControlIdentifier(image?.control_group ?? image?.control);
}

function isControlImage(image) {
  return String(image?.type ?? '').trim().toLowerCase() === 'control'
    || image?.control_group != null
    || image?.control != null;
}

function slideKey(organizationId, identifier) {
  return `${organizationId}--${identifier}`;
}

function memberKey(image) {
  return image?.hash || image?.id || image?.key || null;
}

export function buildControlSlides({ organizations = [], images = [], records = [] } = {}) {
  const organizationNames = new Map(
    organizations.map((organization) => [
      organization.id,
      organization.name || organization.id,
    ]),
  );
  const slides = new Map();

  records.forEach((record) => {
    const organizationId = String(record?.organization_id ?? '').trim();
    const identifier = normalizeControlIdentifier(record?.control_identifier);
    if (!organizationId || identifier == null) {
      return;
    }
    slides.set(slideKey(organizationId, identifier), {
      key: slideKey(organizationId, identifier),
      recordId: record.id || slideKey(organizationId, identifier),
      organizationId,
      organizationName: organizationNames.get(organizationId) || organizationId,
      identifier,
      description: String(record.description ?? '').trim(),
      explicit: true,
      enabled: record.enabled !== false,
      membersByKey: new Map(),
    });
  });

  images.forEach((image) => {
    if (!isControlImage(image)) {
      return;
    }
    const organizationId = String(image?.organization_id ?? '').trim();
    const identifier = controlIdentifierForImage(image);
    const imageKey = memberKey(image);
    if (!organizationId || identifier == null || !imageKey) {
      return;
    }
    const key = slideKey(organizationId, identifier);
    if (!slides.has(key)) {
      slides.set(key, {
        key,
        recordId: null,
        organizationId,
        organizationName: organizationNames.get(organizationId) || organizationId,
        identifier,
        description: '',
        explicit: false,
        enabled: true,
        membersByKey: new Map(),
      });
    }
    slides.get(key).membersByKey.set(imageKey, image);
  });

  return Array.from(slides.values())
    .map(({ membersByKey, ...slide }) => {
      const imagesForSlide = Array.from(membersByKey.values()).sort((left, right) => (
        (normalizeControlIdentifier(left.control) ?? Number.MAX_SAFE_INTEGER)
          - (normalizeControlIdentifier(right.control) ?? Number.MAX_SAFE_INTEGER)
        || String(left.hash || left.id || '').localeCompare(String(right.hash || right.id || ''))
      ));
      const imageCount = imagesForSlide.length;
      const ready = imageCount === CONTROL_SLIDE_IMAGE_COUNT;
      return {
        ...slide,
        images: imagesForSlide,
        imageCount,
        ready,
        released: slide.enabled,
      };
    })
    .sort((left, right) => (
      left.organizationName.localeCompare(right.organizationName)
      || left.identifier - right.identifier
    ));
}

export function validateControlSlideInput({ organizationId, controlIdentifier, files } = {}) {
  const errors = [];
  const selectedFiles = Array.from(files || []);
  if (!String(organizationId ?? '').trim()) {
    errors.push('Select an organization.');
  }
  if (normalizeControlIdentifier(controlIdentifier) == null) {
    errors.push('Enter a positive whole-number control identifier.');
  }
  if (selectedFiles.length !== CONTROL_SLIDE_IMAGE_COUNT) {
    errors.push(`Select exactly ${CONTROL_SLIDE_IMAGE_COUNT} images.`);
  }
  if (selectedFiles.length > 0 && selectedFiles.some((file) => !file.type?.startsWith('image/'))) {
    errors.push('Every file must be an image.');
  }
  return errors;
}
